import { Capacitor } from "@capacitor/core"

const nativeProtocols = ["capacitor:", "ionic:"]

export function getApiBaseUrl() {
	const configuredBaseUrl =
		import.meta.env.VITE_BASE_URL || "http://localhost:3000"
	const isNativeOrigin =
		typeof window !== "undefined" &&
		nativeProtocols.includes(window.location.protocol)

	if (Capacitor.isNativePlatform() || isNativeOrigin) {
		return configuredBaseUrl
	}

	if (typeof window !== "undefined") return window.location.origin

	return configuredBaseUrl
}

export function getAppUrl(path = "/") {
	const isNativeOrigin =
		typeof window !== "undefined" &&
		nativeProtocols.includes(window.location.protocol)
	const baseUrl =
		Capacitor.isNativePlatform() ||
		isNativeOrigin ||
		typeof window === "undefined"
			? getApiBaseUrl()
			: window.location.origin
	const url = new URL(path, baseUrl)

	if (nativeProtocols.includes(url.protocol)) {
		return new URL(
			`${url.pathname}${url.search}${url.hash}`,
			getApiBaseUrl()
		).toString()
	}

	return url.toString()
}

export function getWebAuthUrls(callbackURL = "/") {
	const callback = new URL(callbackURL, window.location.origin)
	const configuredBaseUrl = import.meta.env.VITE_BASE_URL
	const configuredOrigin = configuredBaseUrl
		? new URL(configuredBaseUrl).origin
		: window.location.origin

	if (callback.origin !== configuredOrigin) {
		const bridge = new URL("/api/custom-domain-auth/complete", configuredOrigin)
		bridge.searchParams.set("redirect", callback.toString())
		const errorBridge = new URL(
			"/api/custom-domain-auth/error",
			configuredOrigin
		)
		errorBridge.searchParams.set("redirect", callback.toString())
		return {
			callbackURL: `${bridge.pathname}${bridge.search}`,
			errorCallbackURL: `${errorBridge.pathname}${errorBridge.search}`
		}
	}

	return {
		callbackURL: `${callback.pathname}${callback.search}${callback.hash}`,
		errorCallbackURL: "/unauthorized"
	}
}

export function getWebAuthProxyStartUrl({
	provider,
	callbackURL,
	errorCallbackURL,
	email
}: {
	provider: string
	callbackURL: string
	errorCallbackURL: string
	email?: string
}) {
	const configuredBaseUrl = import.meta.env.VITE_BASE_URL
	if (
		!configuredBaseUrl ||
		typeof window === "undefined" ||
		window.location.origin === new URL(configuredBaseUrl).origin ||
		!callbackURL.startsWith("/api/custom-domain-auth/")
	) {
		return null
	}

	const url = new URL("/auth-proxy", configuredBaseUrl)
	url.searchParams.set("provider", provider)
	url.searchParams.set("callbackURL", callbackURL)
	url.searchParams.set("errorCallbackURL", errorCallbackURL)
	if (email) url.searchParams.set("email", email)
	return url.toString()
}

export function getNativeAuthCallbackUrl(path = "/", authState?: string) {
	const url = new URL("/api/native-auth/callback", getApiBaseUrl())
	url.searchParams.set("redirect", path)
	if (authState) url.searchParams.set("state", authState)
	return url.toString()
}

export function getNativeAuthStartUrl({
	provider,
	callbackURL,
	errorCallbackURL,
	email
}: {
	provider: string
	callbackURL: string
	errorCallbackURL: string
	email?: string
}) {
	const url = new URL("/native-auth", getApiBaseUrl())
	url.searchParams.set("provider", provider)
	url.searchParams.set("callbackURL", callbackURL)
	url.searchParams.set("errorCallbackURL", errorCallbackURL)
	if (email) url.searchParams.set("email", email)
	return url.toString()
}

export function getNativeAuthHandoffUrl({
	code,
	provider,
	callbackURL,
	errorCallbackURL
}: {
	code: string
	provider: string
	callbackURL: string
	errorCallbackURL: string
}) {
	const url = new URL("/api/native-auth/link", getApiBaseUrl())
	url.searchParams.set("code", code)
	url.searchParams.set("provider", provider)
	url.searchParams.set("callbackURL", callbackURL)
	url.searchParams.set("errorCallbackURL", errorCallbackURL)
	return url.toString()
}
